/**
 * UnmergePanel - Slide drawer for unmerging a source from a canonical fish species
 * Allows user to select which field values to keep for the new canonical record
 */
import { useState, useEffect } from 'react';
import {
  Drawer,
  Stack,
  Title,
  Text,
  Button,
  Group,
  Alert,
  Loader,
  Center,
  Divider,
  ScrollArea,
} from '@mantine/core';
import { IconAlertCircle, IconUnlink } from '@tabler/icons-react';
import { fishSpeciesApi } from '@/api/fishSpecies';
import type { FishCanonicalWithSources, FishSourceDetail } from '@/types/livestock';
import { FieldComparisonRow } from './FieldComparisonRow';

// Fields that can be customized during unmerge
const MERGEABLE_FIELDS = [
  { name: 'common_name', label: 'Common Name' },
  { name: 'scientific_name', label: 'Scientific Name' },
  { name: 'family', label: 'Family' },
  { name: 'category', label: 'Category' },
  { name: 'care_level', label: 'Care Level' },
  { name: 'temperament', label: 'Temperament' },
  { name: 'diet', label: 'Diet' },
  { name: 'reef_compatible', label: 'Reef Compatible' },
  { name: 'max_size_inches', label: 'Max Size (inches)' },
  { name: 'min_tank_size_gallons', label: 'Min Tank Size (gallons)' },
  { name: 'description', label: 'Description' },
  { name: 'image_url', label: 'Image URL' },
] as const;

type MergeableFieldName = (typeof MERGEABLE_FIELDS)[number]['name'];

interface UnmergePanelProps {
  opened: boolean;
  onClose: () => void;
  canonicalId: number;
  sourceToUnmerge: string; // "LiveAquaria" | "AbyssAquatics" | "TMC"
  onSuccess: (newCanonicalId: number) => void;
}

export function UnmergePanel({
  opened,
  onClose,
  canonicalId,
  sourceToUnmerge,
  onSuccess,
}: UnmergePanelProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [canonical, setCanonical] = useState<FishCanonicalWithSources | null>(null);
  const [sourceDetail, setSourceDetail] = useState<FishSourceDetail | null>(null);
  const [fieldSelections, setFieldSelections] = useState<Record<string, string>>({});

  // Load canonical with sources when opened
  useEffect(() => {
    if (opened && canonicalId && sourceToUnmerge) {
      setLoading(true);
      setError(null);

      fishSpeciesApi
        .getCanonicalWithSources(canonicalId)
        .then((data) => {
          setCanonical(data);
          // Find the source to unmerge from the fetched sources
          const source = data.sources.find((s) => s.source_name === sourceToUnmerge);
          if (!source) {
            throw new Error(`Source ${sourceToUnmerge} not found in canonical ${canonicalId}`);
          }
          setSourceDetail(source);
          // Pre-select the source being unmerged for all fields
          const initialSelections: Record<string, string> = {};
          for (const field of MERGEABLE_FIELDS) {
            initialSelections[field.name] = sourceToUnmerge;
          }
          setFieldSelections(initialSelections);
        })
        .catch((err) => {
          setError(err.message || 'Failed to load canonical data');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [opened, canonicalId, sourceToUnmerge]);

  // Reset state when drawer closes
  useEffect(() => {
    if (!opened) {
      setCanonical(null);
      setSourceDetail(null);
      setFieldSelections({});
      setError(null);
    }
  }, [opened]);

  const handleFieldSelect = (fieldName: string, source: string) => {
    setFieldSelections((prev) => ({
      ...prev,
      [fieldName]: source,
    }));
  };

  const handleConfirmUnmerge = async () => {
    if (!canonicalId || !sourceDetail) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await fishSpeciesApi.unmergeSource({
        canonical_id: canonicalId,
        source_fish_id: sourceDetail.id,
        field_selections: fieldSelections,
      });
      onSuccess(response.new_canonical_id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unmerge source');
    } finally {
      setSubmitting(false);
    }
  };

  // Build field values for comparison rows
  const getFieldValues = (fieldName: MergeableFieldName) => {
    if (!canonical || !sourceDetail) return [];

    const values = [
      {
        source: 'canonical',
        value: canonical[fieldName],
        label: 'Current (Canonical)',
      },
      {
        source: sourceDetail.source_name,
        value: sourceDetail[fieldName],
        label: sourceDetail.source_name,
      },
    ];

    return values;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Center h={200} data-testid="loading-state">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading source data...</Text>
          </Stack>
        </Center>
      );
    }

    if (error) {
      return (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          data-testid="error-state"
        >
          {error}
        </Alert>
      );
    }

    if (!canonical || !sourceDetail) {
      return null;
    }

    return (
      <Stack gap="md">
        <Alert color="blue" variant="light">
          <Text size="sm">
            Select which values to use for the new separate record. The source being
            unmerged (<strong>{sourceDetail.source_name}</strong>) will become its own
            canonical species.
          </Text>
        </Alert>

        <Divider label="Field Values" labelPosition="center" />

        <ScrollArea.Autosize mah="calc(100vh - 350px)" offsetScrollbars>
          <Stack gap="xs">
            {MERGEABLE_FIELDS.map((field) => (
              <FieldComparisonRow
                key={field.name}
                fieldName={field.name}
                fieldLabel={field.label}
                values={getFieldValues(field.name)}
                selectedSource={fieldSelections[field.name] || sourceDetail.source_name}
                onSelect={(source) => handleFieldSelect(field.name, source)}
              />
            ))}
          </Stack>
        </ScrollArea.Autosize>

        <Divider />

        <Group justify="flex-end" gap="md">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="red"
            leftSection={<IconUnlink size={16} />}
            onClick={handleConfirmUnmerge}
            loading={submitting}
            data-testid="confirm-unmerge-button"
          >
            Confirm Unmerge
          </Button>
        </Group>
      </Stack>
    );
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        <Group gap="sm">
          <IconUnlink size={20} />
          <Title order={4}>Unmerge Source</Title>
        </Group>
      }
      padding="md"
    >
      {renderContent()}
    </Drawer>
  );
}
